import axiosClient from "./axios-client/axiosClient.jsx";

/* TerminalAPI — thin client for the §A–§E endpoints defined in
   BACKEND_TERMINAL_SPEC.md. All methods return the raw axios
   response so the calling hook can read `data` / handle status
   codes. Each method matches the spec verbatim — paths, query
   params, and bodies are 1:1 with what the back team built.

   Error handling lives in the per-pane hooks: every hook treats
   a 404 (feature flag off) or 5xx (backend degraded) as "fall back
   to mock data" so the UI never goes blank. */

const idempotencyKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const authIdem = (token, key) => ({
  headers: {
    Authorization: `Bearer ${token}`,
    "Idempotency-Key": key || idempotencyKey(),
    "Content-Type": "application/json",
  },
});

class TerminalAPI {
  /* ── §A — Overview ─────────────────────────────────────────── */

  /* A.1 — country net-flow (24h). */
  getGlobalFlow24h(token, since) {
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    return axiosClient.get(`/treasury/global_flow_24h${qs}`, auth(token));
  }

  /* A.2 — active corridors. */
  getCorridorsActive(token, since) {
    const qs = since ? `?since=${encodeURIComponent(since)}` : "";
    return axiosClient.get(`/treasury/corridors_active${qs}`, auth(token));
  }

  /* A.3 — settlement timeline (next 72h). */
  getSettlementTimeline(token, { fromHours = -2, toHours = 72 } = {}) {
    return axiosClient.get(
      `/treasury/settlement_timeline?from_hours=${fromHours}&to_hours=${toHours}`,
      auth(token),
    );
  }

  /* A.4 — in-flight payments. */
  getInflight(token) {
    return axiosClient.get(`/treasury/inflight`, auth(token));
  }

  /* A.5 — top counterparties (30d aggregate). */
  getCounterpartiesTop(token, { windowDays = 30, limit = 10 } = {}) {
    return axiosClient.get(
      `/treasury/counterparties_top?window_days=${windowDays}&limit=${limit}`,
      auth(token),
    );
  }

  /* A.6 — FX windows. */
  getFxWindows(token) {
    return axiosClient.get(`/treasury/fx_windows`, auth(token));
  }

  /* ── §B — Pay ──────────────────────────────────────────────── */

  /* B.1 — vendors w/ due_amount, payment_terms, saved_methods[]. */
  getVendorsWithDue(token) {
    return axiosClient.get(`/vendors?include_due=true`, auth(token));
  }

  /* B.2 — bills */
  listBills(token) { return axiosClient.get(`/bills`, auth(token)); }
  createBill(token, payload, key) { return axiosClient.post(`/bills`, payload, authIdem(token, key)); }
  getBill(token, id) { return axiosClient.get(`/bills/${id}`, auth(token)); }
  updateBill(token, id, fields, key) { return axiosClient.post(`/bills/${id}/update`, fields, authIdem(token, key)); }
  skipBill(token, id, key) { return axiosClient.post(`/bills/${id}/skip_next`, {}, authIdem(token, key)); }
  payBillNow(token, id, key) { return axiosClient.post(`/bills/${id}/pay_now`, {}, authIdem(token, key)); }
  deleteBill(token, id) { return axiosClient.post(`/bills/${id}/delete`, {}, auth(token)); }
  scheduleBill(token, id, body, key) { return axiosClient.post(`/bills/${id}/schedule`, body, authIdem(token, key)); }

  /* B.3 — bulk batch with policy_checks + rail_mix in response. */
  getBatch(token, batchId) {
    return axiosClient.get(`/bulk_pay/batches/${batchId}`, auth(token));
  }

  /* B.4 — scheduled batches. */
  listScheduledBatches(token) { return axiosClient.get(`/bulk_pay/scheduled`, auth(token)); }
  scheduleBatch(token, batchId, body, key) {
    return axiosClient.post(`/bulk_pay/batches/${batchId}/schedule`, body, authIdem(token, key));
  }
  pauseScheduled(token, scheduleId) { return axiosClient.post(`/bulk_pay/scheduled/${scheduleId}/pause`, {}, auth(token)); }
  resumeScheduled(token, scheduleId) { return axiosClient.post(`/bulk_pay/scheduled/${scheduleId}/resume`, {}, auth(token)); }
  cancelScheduled(token, scheduleId) { return axiosClient.post(`/bulk_pay/scheduled/${scheduleId}/cancel`, {}, auth(token)); }

  /* ── §C — Receive ─────────────────────────────────────────── */
  /* C.1 — already covered by ReceivablesAPI.getChannel which now
     returns accounts[]; we call that directly through the existing
     client to avoid duplicating channel logic. */

  /* C.2 — channel-wide payment link. */
  createChannelPaymentLink(token, channelId, body, key) {
    return axiosClient.post(`/receivables/channels/${channelId}/payment_link`, body, authIdem(token, key));
  }

  /* C.3 — account management within a channel. */
  attachAccount(token, channelId, body) {
    return axiosClient.post(`/receivables/channels/${channelId}/accounts`, body, auth(token));
  }
  updateAccount(token, channelId, accountId, body) {
    return axiosClient.post(`/receivables/channels/${channelId}/accounts/${accountId}/update`, body, auth(token));
  }
  setPrimaryAccount(token, channelId, accountId) {
    return axiosClient.post(`/receivables/channels/${channelId}/accounts/${accountId}/set_primary`, {}, auth(token));
  }
  deleteAccount(token, channelId, accountId) {
    return axiosClient.post(`/receivables/channels/${channelId}/accounts/${accountId}/delete`, {}, auth(token));
  }

  /* C.4 — routing rules. */
  listRoutingRules(token, channelId) {
    return axiosClient.get(`/receivables/channels/${channelId}/routing_rules`, auth(token));
  }
  setRoutingRules(token, channelId, rules) {
    return axiosClient.post(`/receivables/channels/${channelId}/routing_rules`, rules, auth(token));
  }

  /* ── §D — Treasury ────────────────────────────────────────── */

  /* D.1 — group AUM + entities + asset_classes. */
  getGroupAum(token) { return axiosClient.get(`/treasury/group_aum`, auth(token)); }

  /* D.2 — digital holdings. */
  getDigitalHoldings(token) { return axiosClient.get(`/treasury/digital_holdings`, auth(token)); }

  /* D.3 — yield product registry. */
  getYieldProducts(token) { return axiosClient.get(`/treasury/yield_products`, auth(token)); }

  /* D.4 — yield positions + deploy/redeem. */
  getYieldPositions(token) { return axiosClient.get(`/treasury/yield_positions`, auth(token)); }
  yieldDeploy(token, body, key) { return axiosClient.post(`/treasury/yield_deploy`, body, authIdem(token, key)); }
  yieldRedeem(token, body, key) { return axiosClient.post(`/treasury/yield_redeem`, body, authIdem(token, key)); }

  /* D.5 — borrow / lend / repay. */
  borrowQuote(token, body) { return axiosClient.post(`/treasury/borrow_quote`, body, auth(token)); }
  borrowExecute(token, quoteId, key) { return axiosClient.post(`/treasury/borrow_execute`, { quote_id: quoteId }, authIdem(token, key)); }
  getLoans(token) { return axiosClient.get(`/treasury/loans`, auth(token)); }
  repay(token, loanId, body, key) { return axiosClient.post(`/treasury/loans/${loanId}/repay`, body, authIdem(token, key)); }
  addCollateral(token, loanId, body, key) { return axiosClient.post(`/treasury/loans/${loanId}/add_collateral`, body, authIdem(token, key)); }
  lend(token, body, key) { return axiosClient.post(`/treasury/lend`, body, authIdem(token, key)); }
  getLendTerms(token) { return axiosClient.get(`/treasury/lend_terms`, auth(token)); }

  /* D.6 — move (intra/inter-entity). */
  moveQuote(token, body) { return axiosClient.post(`/treasury/move_quote`, body, auth(token)); }
  moveExecute(token, quoteId, key) { return axiosClient.post(`/treasury/move_execute`, { quote_id: quoteId }, authIdem(token, key)); }

  /* D.7 — sweep rules. */
  getSweepRules(token) { return axiosClient.get(`/treasury/sweep_rules`, auth(token)); }
  createSweepRule(token, body) { return axiosClient.post(`/treasury/sweep_rules`, body, auth(token)); }
  updateSweepRule(token, id, fields) { return axiosClient.post(`/treasury/sweep_rules/${id}/update`, fields, auth(token)); }
  deleteSweepRule(token, id) { return axiosClient.post(`/treasury/sweep_rules/${id}/delete`, {}, auth(token)); }
  sweepNow(token, key) { return axiosClient.post(`/treasury/sweep_now`, {}, authIdem(token, key)); }

  /* D.8 — FX exposure + forward + rules. */
  getFxExposure(token) { return axiosClient.get(`/treasury/fx_exposure`, auth(token)); }
  forwardQuote(token, body) { return axiosClient.post(`/treasury/fx_forward_quote`, body, auth(token)); }
  forwardExecute(token, quoteId, key) { return axiosClient.post(`/treasury/fx_forward_execute`, { quote_id: quoteId }, authIdem(token, key)); }
  getFxRules(token) { return axiosClient.get(`/treasury/fx_rules`, auth(token)); }
  setFxRules(token, rules) { return axiosClient.post(`/treasury/fx_rules`, rules, auth(token)); }

  /* D.9 — custody + chain exposure. */
  getCustody(token) { return axiosClient.get(`/treasury/custody`, auth(token)); }
  getChainExposure(token) { return axiosClient.get(`/treasury/chain_exposure`, auth(token)); }

  /* D.10 — approvals + breaches. */
  listApprovals(token, status = "pending") {
    return axiosClient.get(`/treasury/approvals?status=${encodeURIComponent(status)}`, auth(token));
  }
  approveApproval(token, id, comment, key) {
    return axiosClient.post(`/treasury/approvals/${id}/approve`, { comment: comment || "" }, authIdem(token, key));
  }
  rejectApproval(token, id, comment, key) {
    return axiosClient.post(`/treasury/approvals/${id}/reject`, { comment: comment || "" }, authIdem(token, key));
  }
  listBreaches(token, status = "active") {
    return axiosClient.get(`/treasury/breaches?status=${encodeURIComponent(status)}`, auth(token));
  }
  ackBreach(token, id) { return axiosClient.post(`/treasury/breaches/${id}/acknowledge`, {}, auth(token)); }
  approveBreach(token, id) { return axiosClient.post(`/treasury/breaches/${id}/approve_breach`, {}, auth(token)); }

  /* D.11 — server-side NL command parser (optional). */
  commandParse(token, input) {
    return axiosClient.post(`/treasury/command_parse`, { input }, auth(token));
  }

  /* ── §E — Cross-cutting ───────────────────────────────────── */

  /* E.1 — live status spine. */
  getCockpitState(token) { return axiosClient.get(`/treasury/cockpit_state`, auth(token)); }
}

export const terminalAPI = new TerminalAPI();
export default terminalAPI;
